import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { toast } from "sonner";
import { Loader2, Plus, RefreshCw, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { vendorPortalApi, type VendorInvoice, type VendorInvoiceStatus } from "@/api/vendorPortal";
import { formatDate } from "@/utils/dateUtils";

function statusBadge(status: VendorInvoiceStatus) {
  const map: Record<VendorInvoiceStatus, string> = {
    DRAFT: "bg-slate-100 text-slate-800",
    PENDING_OPS: "bg-amber-100 text-amber-900",
    PENDING_ADMIN: "bg-sky-100 text-sky-900",
    REJECTED: "bg-red-100 text-red-800",
    APPROVED: "bg-violet-100 text-violet-900",
    PAID: "bg-emerald-100 text-emerald-900",
  };
  return <Badge className={map[status]}>{status.replace(/_/g, " ")}</Badge>;
}

function money(n: number) {
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function VendorDashboardPage() {
  const qc = useQueryClient();
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [form, setForm] = useState({
    invoiceNumber: "",
    invoiceDate: format(new Date(), "yyyy-MM-dd"),
    amount: "",
    description: "",
  });
  const [file, setFile] = useState<File | null>(null);

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["vendor-me"],
    queryFn: async () => (await vendorPortalApi.myProfile()).data.data,
  });

  const { data: invoices = [], isLoading, isFetching, refetch } = useQuery({
    queryKey: ["vendor-my-invoices"],
    queryFn: async () => (await vendorPortalApi.listInvoices()).data.data,
  });

  const summary = useMemo(() => {
    const unpaid = invoices.filter((i) => i.status !== "PAID" && i.status !== "REJECTED");
    const paid = invoices.filter((i) => i.status === "PAID");
    return {
      total: invoices.length,
      pending: unpaid.length,
      paidAmount: paid.reduce((s, i) => s + i.amount, 0),
      pendingAmount: unpaid.reduce((s, i) => s + i.amount, 0),
    };
  }, [invoices]);

  const createMut = useMutation({
    mutationFn: async () => {
      const amount = Number(form.amount);
      if (!form.invoiceNumber.trim()) throw new Error("Invoice number required");
      if (!Number.isFinite(amount) || amount <= 0) throw new Error("Valid amount required");
      if (!file) throw new Error("Upload the invoice file before submitting");
      const created = await vendorPortalApi.createInvoice({
        invoiceNumber: form.invoiceNumber.trim(),
        invoiceDate: form.invoiceDate,
        amount,
        description: form.description || undefined,
        submit: false,
      });
      const inv = created.data.data;
      if (file) {
        await vendorPortalApi.uploadInvoiceFile(inv.id, file);
      }
      await vendorPortalApi.submitInvoice(inv.id);
      return inv;
    },
    onSuccess: () => {
      toast.success("Invoice submitted");
      setRaiseOpen(false);
      setForm({
        invoiceNumber: "",
        invoiceDate: format(new Date(), "yyyy-MM-dd"),
        amount: "",
        description: "",
      });
      setFile(null);
      void qc.invalidateQueries({ queryKey: ["vendor-my-invoices"] });
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { message?: string } }; message?: string };
      toast.error(ax.response?.data?.message || ax.message || "Failed to submit invoice");
    },
  });

  if (profileLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Vendor portal</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {profile?.name}
            {profile?.requiresOpsApproval
              ? " · Invoices need Operations Manager approval before admin payment"
              : " · Invoices go directly to hospital admin for payment"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setRaiseOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Raise invoice
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Invoices</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-semibold">{summary.total}</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pending / in process</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-amber-800">{money(summary.pendingAmount)}</p>
            <p className="text-xs text-muted-foreground">{summary.pending} invoice(s)</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Paid</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-emerald-800">{money(summary.paidAmount)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">My invoices</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground">No invoices yet. Raise your first invoice.</p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status / payment</TableHead>
                      <TableHead>File</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((row: VendorInvoice) => (
                      <TableRow key={row.id}>
                        <TableCell className="font-medium">{row.invoiceNumber}</TableCell>
                        <TableCell>{formatDate(row.invoiceDate)}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(row.amount)}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {statusBadge(row.status)}
                            {row.centralExpense?.paymentStatus ? (
                              <span className="text-xs text-muted-foreground">
                                Expense: {row.centralExpense.paymentStatus}
                                {row.centralExpense.paidAt
                                  ? ` · ${formatDate(row.centralExpense.paidAt)}`
                                  : ""}
                              </span>
                            ) : null}
                            {row.rejectReason ? (
                              <span className="text-xs text-red-700">{row.rejectReason}</span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>
                          {row.fileUrl ? (
                            <a
                              href={row.fileUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm text-sky-700 underline"
                            >
                              View
                            </a>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={raiseOpen} onOpenChange={setRaiseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Raise invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Invoice number</Label>
              <Input
                value={form.invoiceNumber}
                onChange={(e) => setForm((f) => ({ ...f, invoiceNumber: e.target.value }))}
              />
            </div>
            <div>
              <Label>Invoice date</Label>
              <Input
                type="date"
                value={form.invoiceDate}
                onChange={(e) => setForm((f) => ({ ...f, invoiceDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div>
              <Label>Invoice file (PDF / image)</Label>
              <Input
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <Upload className="h-3 w-3" /> Required — images compressed to max 200 KB
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRaiseOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={createMut.isPending || !file}
              onClick={() => createMut.mutate()}
            >
              {createMut.isPending ? "Submitting…" : "Submit invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
